import { SearchOutlined } from '@ant-design/icons';
import { Alert, Input, Segmented, Space } from 'antd';
import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useStartupSession } from '@/app/session/StartupSessionContext';
import { useAppRefreshHandler } from '@/app/shell/app-refresh';
import { StatsRow } from '@/components/StatsRow';
import { Commodity } from '@/features/inventorization/domain/commodity';
import { formatInventorizationCommodity } from '@/features/inventorization/domain/inventorization-identity';
import { useLocationBootstrap } from '@/features/location-bootstrap/LocationBootstrapContext';
import { cacheId, toMeterSearchData } from '@/features/location-bootstrap/location-bootstrap-cache-records';
import {
  createCombinedMeterList,
  filterMeters,
  filterMetersByCommodity,
} from '@/features/meters/meter-record-list-model';
import { useInventoryMeterSessions } from '@/features/meters/useInventoryMeterSessions';
import { MeterRecordsList } from '@/routers/meters/components/MeterRecordsList';
import { CAPTURE_SESSION_STATUSES } from '@/shared/capture/capture-session';
import { translateInventoryText } from '@/shared/i18n/inventory-i18n';
import { METER_IDENTIFIER_KINDS, type MeterSearchResult } from '@/shared/meter-identification';
import { messages } from './messages';

interface MetersViewProps {
  isOpeningMeter?: boolean;
  onOpenMeter: (meter: MeterSearchResult) => Promise<void> | void;
  onOpenQueuedMeter: (sessionId: string) => void;
}

const meterCommodityFilters = [
  '',
  Commodity.Electricity,
  Commodity.Water,
  Commodity.NaturalGas,
  Commodity.Thermal,
  Commodity.Other,
] as const;
type MeterCommodityFilter = (typeof meterCommodityFilters)[number];

export function MetersView({ isOpeningMeter = false, onOpenMeter, onOpenQueuedMeter }: MetersViewProps) {
  const intl = useIntl();
  const { activeLocation } = useStartupSession();
  const {
    bootstrap,
    error: bootstrapError,
    isLoading: isLoadingBootstrap,
    refresh: refreshBootstrap,
  } = useLocationBootstrap();
  const {
    deleteLocalDraft,
    deletingSessionId,
    error: sessionsError,
    isLoading: isLoadingSessions,
    refresh: refreshSessions,
    sessions,
  } = useInventoryMeterSessions({ locationId: activeLocation?.locationId });
  const [query, setQuery] = useState('');
  const [commodityFilter, setCommodityFilter] = useState<MeterCommodityFilter>('');
  const meters = useMemo<MeterSearchResult[]>(
    () =>
      bootstrap
        ? bootstrap.existingMeters.map(meter => ({
            data: toMeterSearchData(bootstrap, meter),
            id: cacheId(bootstrap.locationId, 'meter', meter._id),
            matchedBy: METER_IDENTIFIER_KINDS.unknown,
          }))
        : [],
    [bootstrap],
  );
  const combinedMeters = useMemo(() => createCombinedMeterList(sessions, meters), [meters, sessions]);
  const commodityMeters = useMemo(
    () => filterMetersByCommodity(combinedMeters, commodityFilter),
    [combinedMeters, commodityFilter],
  );
  const visibleMeters = useMemo(() => filterMeters(commodityMeters, query), [commodityMeters, query]);
  const queuedCount = sessions.filter(session => session.status === CAPTURE_SESSION_STATUSES.queued).length;
  const downloadedCount = meters.length;

  useAppRefreshHandler(() => Promise.all([refreshBootstrap(), refreshSessions()]));

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {bootstrapError ? <Alert closable message={bootstrapError} showIcon type="warning" /> : null}
      {sessionsError ? <Alert closable message={sessionsError} showIcon type="warning" /> : null}

      <StatsRow
        items={[
          { label: intl.formatMessage(messages.metersQueuedLocal), value: queuedCount },
          { label: intl.formatMessage(messages.metersSavedInDb), value: downloadedCount },
          { label: intl.formatMessage(messages.metersVisible), value: visibleMeters.length },
        ]}
      />

      <Segmented<MeterCommodityFilter>
        block
        onChange={setCommodityFilter}
        options={meterCommodityFilters.map(value => ({
          label: value
            ? formatInventorizationCommodity(value)
            : translateInventoryText('app.inventory.meters.filters.all', 'All'),
          value,
        }))}
        value={commodityFilter}
      />

      <Input.Search
        allowClear
        enterButton={false}
        onChange={event => setQuery(event.target.value)}
        placeholder={intl.formatMessage(messages.metersSearchPlaceholder)}
        prefix={<SearchOutlined />}
        value={query}
      />

      <MeterRecordsList
        deletingSessionId={deletingSessionId}
        isLoading={isLoadingBootstrap || isLoadingSessions}
        isOpeningMeter={isOpeningMeter}
        items={visibleMeters}
        onDeleteLocalDraft={deleteLocalDraft}
        onOpenMeter={onOpenMeter}
        onOpenQueuedMeter={onOpenQueuedMeter}
      />
    </Space>
  );
}
